// Insurance A/R — in-memory mock store. No network (INSURANCE_AR_HANDOFF §8).
import { useSyncExternalStore } from 'react';
import type {
  ArBucket,
  ArClaim,
  ArPayerLine,
  ArStopped,
  BilledWhy,
  FollowUpSettings,
  StopReason,
  WriteOffReason,
  WriteOffRow,
} from './types';
import { buildClaims, buildSettings, buildWrittenOff } from './fixtures';
import {
  AR_TODAY_ISO,
  IAR_ACTOR_DOT,
  IAR_USER,
  PAUSE_DEFAULT_DAYS,
  WRITE_OFF_REASON_WORDS,
  isoLabel,
  isoPlusDays,
  money,
  moneyFor,
  nextBusinessDayIso,
} from './utils';
import { AR_PAYERS, arPayerHref, payerShort, payerSlug, AR_LATE_HREF, AR_PAYS_PATIENT_HREF } from './payers';
import { keepGoingDates, nextCheckDate, todayLabel } from './schedule';
import { logEntryIdFor } from './logLinks';
import { MOCK_NOW } from '@/components/lists';
import { isPaidToPatientPosted } from '@/features/networks/paidToPatient';
import {
  addClaimDecideItem,
  getItemSync as getHitlItemSync,
  subscribe as hitlSubscribe,
} from '@/features/human-in-the-loop/store';

export const WORK_MS = 900;

type Scenario = '' | 'limit-1' | 'empty' | 'line-under-appeal' | 'found-elsewhere';

interface State {
  claims: ArClaim[];
  writtenOff: WriteOffRow[];
  settings: FollowUpSettings;
  scenario: Scenario;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (raw === 'limit-1' || raw === 'empty' || raw === 'line-under-appeal' || raw === 'found-elsewhere') return raw;
  } catch {
    /* no window */
  }
  return '';
}

function initState(): State {
  const scenario = readScenario();
  const settings = buildSettings();
  const claims = buildClaims();
  const writtenOff = buildWrittenOff();

  if (scenario === 'empty') {
    return { claims: [], writtenOff, settings, scenario };
  }
  if (scenario === 'limit-1') settings.limit = 1;
  if (scenario === 'line-under-appeal') {
    // Victor Lang's claim has another line under appeal, so ELVA can't close it (§5.1).
    const victor = claims.find((c) => c.id === 'AR-VICTOR');
    if (victor) victor.money = { ...moneyFor(victor), closeBlockedBy: 'the extraction is under appeal' };
  }

  return { claims, writtenOff, settings, scenario };
}

const state: State = initState();

// ---- the follow-up limit (§4.1, D117, D159) ----
// Real Human in the loop items that already exist for the curated claims at the
// limit; every other claim that reaches it gets its own item (one each).
const KNOWN_HITL_ITEMS: Record<string, string> = { 'AR-SANA': 'HL-SANA', 'AR-VICTOR': 'HL-VICTOR' };

/** Rounds at which a claim is "at the limit": a kept-going claim runs a full new set (D159). */
function threshold(c: ArClaim, limit: number): number {
  return (c.keptGoing?.fromRan ?? 0) + limit;
}

/** "43 days by date of service · 41 days since sent": the aging cards count the first (D203). */
function ageText(c: ArClaim): string {
  return `${String(c.serviceDays ?? c.days)} days by date of service · ${String(c.days)} days since sent`;
}

function times(n: number): string {
  return n === 1 ? 'once' : `${String(n)} times`;
}

function lastFollowUpDate(c: ArClaim): string {
  for (let i = c.tries.length - 1; i >= 0; i--) {
    const t = c.tries[i];
    if (t?.head.includes('Follow-up')) return t.date;
  }
  return c.sentDisplay;
}

/** Put a claim in "Needs a person": no next check, linked to its own HITL item. */
function toLimitState(c: ArClaim, itemId: string, limit: number): void {
  const ran = c.ran ?? 0;
  const short = c.payer.short;
  c.hitlItemId = itemId;
  c.decisionApplied = false;
  delete c.nextCheck;
  c.round = { n: Math.min(ran, limit), limit };
  c.status = {
    kind: 'limit',
    headline: `ELVA followed up ${times(ran)}. ${short} still hasn’t paid after ${String(c.days)} days.`,
    latest: `On ${lastFollowUpDate(c)} ${short} said the claim is still in process.`,
    next: 'ELVA stops here. Decide in Human in the loop: let ELVA keep following up, or stop and write it off.',
    ...(c.status.deadline ? { deadline: c.status.deadline } : {}),
  };
  c.rowBadge = 'needs';
  c.rowLine = `ELVA followed up ${times(ran)}. It’s in Human in the loop.`;
  c.rowMeta = `${ageText(c)} · in Human in the loop`;
}

function createHitlItem(c: ArClaim): string {
  const suffix = c.keptGoing ? `-${String(c.ran ?? 0)}` : '';
  return addClaimDecideItem({
    id: `HL-AR-${c.id}${suffix}`,
    patient: c.patient,
    patientFirst: c.firstName,
    payer: c.payer.name,
    amount: c.balance,
    days: c.days,
    sent: c.sentDisplay,
    claimNo: c.id,
    rounds: c.ran ?? 0,
    tries: c.tries.map((t) => ({
      time: t.date,
      channel: t.head.replace(/\.$/, ''),
      result: t.body,
      ok: t.icon === 'ok',
      logEntryId: logEntryIdFor(c.payer.name, t),
    })),
    checkDates: keepGoingDates(state.settings.limit),
  });
}

/**
 * Applies the current limit to every waiting claim: any claim at or past it
 * goes to Human in the loop at once, one item each (D159), and the others keep
 * the "follow-up n of {limit}" wording in step with the setting.
 */
function applyLimit(): number {
  let moved = 0;
  const limit = state.settings.limit;
  for (const c of state.claims) {
    if (c.removed || c.group !== 'late' || c.ran === undefined) continue;
    if (c.hitlItemId) continue;
    if (c.ran >= threshold(c, limit)) {
      toLimitState(c, KNOWN_HITL_ITEMS[c.id] ?? createHitlItem(c), limit);
      moved += 1;
    } else if (!c.keptGoing) {
      c.round = { n: Math.min(c.ran, limit), limit };
      const fix = (t: string) => t.replace(/(follow-up )(\d+) of \d+/gi, `$1$2 of ${String(limit)}`);
      if (c.status.next) c.status = { ...c.status, next: fix(c.status.next) };
      c.rowMeta = fix(c.rowMeta);
    }
  }
  return moved;
}

/** A person chose "Keep going" / "Stop" in Human in the loop — apply it here. */
function syncDecisions(): void {
  let changed = false;
  const limit = state.settings.limit;
  for (const c of state.claims) {
    if (!c.hitlItemId || c.decisionApplied || c.removed) continue;
    const item = getHitlItemSync(c.hitlItemId);
    const d = item?.claimDecision;
    if (!item || !d) continue;
    c.decisionApplied = true;
    changed = true;
    if (d.mode === 'keep') {
      const ran = c.ran ?? 0;
      const cd = item.claimDecide?.checkDate;
      const date = cd !== undefined && cd !== '' ? cd : nextCheckDate(c.days);
      const short = c.payer.short;
      delete c.hitlItemId;
      c.keptGoing = { by: d.by, on: d.dateLabel, limit, fromRan: ran };
      c.nextCheck = date;
      c.round = { n: Math.min(ran, limit), limit };
      c.status = {
        kind: 'late',
        headline: `${short} hasn’t paid in ${String(c.days)} days. It’s past ${short}’s usual time.`,
        latest: `${short} says the claim is still in process. ${d.by} chose to let ELVA keep going.`,
        next: `ELVA checks again on ${date}.`,
        ...(c.status.deadline ? { deadline: c.status.deadline } : {}),
      };
      c.rowBadge = 'late';
      c.rowLine = `${short} is past its usual time; kept going by ${d.by}`;
      c.rowMeta = `${ageText(c)} · follow-up ${String(ran)} · next check ${date}`;
    } else {
      const r = d.reason ?? '';
      const reason: StopReason = /patient/i.test(r)
        ? 'patient_pays'
        : /another insurance/i.test(r)
          ? 'billed_elsewhere'
          : /write/i.test(r)
            ? 'write_off'
            : 'other';
      stopFollowUp(
        c.id,
        {
          reason,
          ...(reason === 'billed_elsewhere' ? { otherPayer: 'another insurance', billedWhy: 'forwarded' as const } : {}),
          ...(reason === 'other' ? { note: 'Decided in Human in the loop' } : {}),
        },
        { quiet: true },
      );
    }
  }
  if (changed) emit();
}

applyLimit();
hitlSubscribe(syncDecisions);

// ---- subscription ----
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function useArVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ---- header snapshot ----
// The headline totals and the age pills are computed from the SAME live claim
// records that drive the list (staging fix §7 / §4.3: "Age pills, group counts
// and the list come from the same claim records"). On staging they diverged
// because the header read constant fixture figures while the list read the
// generated records.
export function getScenarioSync(): Scenario {
  return state.scenario;
}
export function getOwedSync(): number {
  return liveClaims().reduce((s, c) => s + c.balance, 0);
}
export function getClaimCountSync(): number {
  return liveClaims().length;
}

const BUCKET_DEFS: { label: string; min: number; max: number; warn: boolean }[] = [
  { label: '0–30 days', min: 0, max: 30, warn: false },
  { label: '31–60 days', min: 31, max: 60, warn: false },
  { label: '61–90 days', min: 61, max: 90, warn: true },
  { label: '91–120 days', min: 91, max: 120, warn: true },
  { label: 'Over 120 days', min: 121, max: Number.POSITIVE_INFINITY, warn: true },
];
/** The five age buckets, counted and summed from the live waiting claims. */
export function getBucketsSync(): ArBucket[] {
  const claims = liveClaims();
  return BUCKET_DEFS.map((d) => {
    // The aging cards count from the date of service (D203).
    const inB = claims.filter((c) => {
      const age = c.serviceDays ?? c.days;
      return age >= d.min && age <= d.max;
    });
    return {
      label: d.label,
      amount: inB.reduce((s, c) => s + c.balance, 0),
      count: inB.length,
      warn: d.warn,
    };
  });
}
// ---- claims ----
/** Claims waiting for payment: the headline, the age buckets and the payer bars count these. */
function liveClaims(): ArClaim[] {
  return state.claims.filter((c) => !c.removed && c.group !== 'pays-patient');
}
/**
 * "The payer pays the patient" (D197): followed, never counted as money the
 * insurer owes. A claim leaves the group once Posting has posted it.
 */
function paysPatientClaims(): ArClaim[] {
  return state.claims.filter((c) => !c.removed && c.group === 'pays-patient' && !isPaidToPatientPosted(c.id));
}
export function getPaysPatientSync(): ArClaim[] {
  return paysPatientClaims().sort((a, b) => a.days - b.days);
}
export function paysPatientCountSync(): number {
  return paysPatientClaims().length;
}
/** Billed (never "owed") across the group, summed from its rows. */
export function paysPatientBilledSync(): number {
  return paysPatientClaims().reduce((s, c) => s + c.billed, 0);
}
/** For the Dashboard's "Not counted" line: how many, and where it links. */
export function getPaysPatientSummary(): { count: number; billed: number; href: string } {
  return { count: paysPatientCountSync(), billed: paysPatientBilledSync(), href: AR_PAYS_PATIENT_HREF };
}
export function getLateSync(): ArClaim[] {
  return liveClaims().filter((c) => c.group === 'late').sort((a, b) => b.days - a.days);
}
export function getOntimeSync(): ArClaim[] {
  return liveClaims().filter((c) => c.group === 'ontime').sort((a, b) => a.days - b.days);
}
/** ELVA isn't following up (Oct 22): paused, "Something else", or billed elsewhere and not found. Still owed. */
export function getNotFollowedSync(): ArClaim[] {
  return liveClaims()
    .filter((c) => c.group === 'not-followed')
    .sort((a, b) => (b.stopped?.onIso ?? '').localeCompare(a.stopped?.onIso ?? ''));
}
export function notFollowedCountSync(): number {
  return getNotFollowedSync().length;
}
export function notFollowedTotalSync(): number {
  return getNotFollowedSync().reduce((s, c) => s + c.balance, 0);
}
export function lateCountSync(): number {
  return getLateSync().length;
}
export function lateTotalSync(): number {
  return getLateSync().reduce((s, c) => s + c.balance, 0);
}
/**
 * Late claims waiting on a person: at the follow-up limit, or a payer that wants
 * office staff. Each carries the "Needs a person" badge (its hitlItemId) and sits
 * in Human in the loop. This is THE "needs you" number for Insurance A/R; the
 * sidebar reads it too (D204), so the two cannot drift.
 */
export function needsYouCountSync(): number {
  return getLateSync().filter((c) => c.hitlItemId !== undefined).length;
}
/** Late claims that sit at the follow-up limit, waiting on a person. */
export function atLimitCountSync(): number {
  return needsYouCountSync();
}

// ---- shared selectors (Dashboard "Owed by insurers", Late row, payer bars) ----
// Everything below reads the SAME live claim records as the list, the age pills
// and the headline, so no page can disagree with another.
export interface ArHeadline {
  /** Total insurers owe: the headline dollar figure. */
  owed: number;
  /** Claims waiting for payment (late + within the usual time). */
  claimCount: number;
  /** "Past the payer's usual time" count — the "{k} are late" figure. */
  lateCount: number;
  lateAmount: number;
  /** Late claims at the follow-up limit that need a person. */
  atLimitCount: number;
}
export function getArHeadline(): ArHeadline {
  return {
    owed: getOwedSync(),
    claimCount: getClaimCountSync(),
    lateCount: lateCountSync(),
    lateAmount: lateTotalSync(),
    atLimitCount: atLimitCountSync(),
  };
}
export interface ArLateSummary {
  count: number;
  amount: number;
  atLimitCount: number;
  oldestDays: number;
  /** `/ar?group=late` — opens A/R with the Late group in view. */
  href: string;
}
export function getLateSummary(): ArLateSummary {
  const late = getLateSync();
  return {
    count: late.length,
    amount: late.reduce((s, c) => s + c.balance, 0),
    atLimitCount: atLimitCountSync(),
    oldestDays: late[0]?.days ?? 0,
    href: AR_LATE_HREF,
  };
}
/** Who owes what, biggest first — the Dashboard's payer bars. Each has an `/ar?payer=` link. */
export function getPayerBreakdown(): ArPayerLine[] {
  const by = new Map<string, ArPayerLine>();
  for (const c of liveClaims()) {
    const name = c.payer.name;
    const cur =
      by.get(name) ??
      {
        payer: name,
        slug: payerSlug(name),
        short: payerShort(name),
        amount: 0,
        count: 0,
        lateCount: 0,
        lateAmount: 0,
        href: arPayerHref([name]),
      };
    cur.amount += c.balance;
    cur.count += 1;
    if (c.group === 'late') {
      cur.lateCount += 1;
      cur.lateAmount += c.balance;
    }
    by.set(name, cur);
  }
  return [...by.values()].sort((a, b) => b.amount - a.amount);
}
/** Same as getPayerBreakdown (kept for existing callers). */
export function getByPayerSync(): ArPayerLine[] {
  return getPayerBreakdown();
}
export { AR_PAYERS };
/**
 * On-time ("within the payer's usual time") is now a full mock queue (00-ds-09
 * §9: 150–900 open), grouped by payer in the UI, so its header reads the live
 * count/total rather than the old partial-mock fixture figures.
 */
export function ontimeCountSync(): number {
  return getOntimeSync().length;
}
export function ontimeTotalSync(): number {
  return getOntimeSync().reduce((s, c) => s + c.balance, 0);
}
/** The header's "Waiting for payment" count: the three groups that are still owed. */
export function waitingCountSync(): number {
  return lateCountSync() + ontimeCountSync() + notFollowedCountSync();
}
export function getClaimSync(id: string): ArClaim | null {
  return [...liveClaims(), ...paysPatientClaims()].find((c) => c.id === id) ?? null;
}

/**
 * Sidebar count (D204) = late claims that need a person (at the follow-up limit, or a payer that
 * wants office staff), never all late claims. Claims in "ELVA isn't following up" never count:
 * nobody has to act on them.
 */
export function useArLateCount(): number {
  return useSyncExternalStore(subscribe, needsYouCountSync, needsYouCountSync);
}

// ---- written off ----
export function getWrittenOffSync(): WriteOffRow[] {
  return state.writtenOff;
}
export function writtenOffCountSync(): number {
  return state.writtenOff.length;
}
export function writtenOffTotalSync(): number {
  return state.writtenOff.reduce((s, r) => s + r.amount, 0);
}

// ---- settings ----
export function getSettingsSync(): FollowUpSettings {
  return state.settings;
}
/** Returns how many claims went to Human in the loop because of the new limit. */
export function saveFollowUpSettings(limit: number): number {
  state.settings.limit = Math.max(1, Math.min(10, limit));
  // D159: lowering the limit sends every claim at or past it to Human in the loop now.
  const moved = applyLimit();
  emit();
  return moved;
}

// ---- actions ----

/** What the Stop dialog hands over (§5.1, Oct 22). There is no amount: the whole open amount, or keep following up. */
export interface StopInput {
  reason: StopReason;
  /** Pause: ISO date the pause ends. Default 30 days from today, at most 90. */
  pauseUntil?: string;
  /** Billed elsewhere: the other insurance (a payer name, or free words from Human in the loop). */
  otherPayer?: string;
  billedWhy?: BilledWhy;
  note?: string;
}

/** Where the claim went: off the list, into "ELVA isn't following up", or it stays where it was. */
export type StopOutcome = 'left' | 'not-followed' | 'stays';

function otherPayerShort(name: string): string {
  return AR_PAYERS.find((p) => p.name === name)?.short ?? name;
}

function moveToNotFollowed(
  claim: ArClaim,
  kind: ArStopped['kind'],
  fill: { line: string; headline: string; bodyLines?: string[]; verb: string; until?: string; nextCheck?: string; note?: string; otherPayer?: string },
): void {
  const on = isoLabel(AR_TODAY_ISO);
  const stopped: ArStopped = {
    kind,
    by: IAR_USER.name,
    on,
    onIso: AR_TODAY_ISO,
    line: fill.line,
    ...(fill.until ? { until: fill.until } : {}),
    ...(fill.nextCheck ? { nextCheck: fill.nextCheck } : {}),
    ...(fill.note ? { note: fill.note } : {}),
    ...(fill.otherPayer ? { otherPayer: fill.otherPayer } : {}),
    prior: {
      group: claim.group,
      status: claim.status,
      rowBadge: claim.rowBadge,
      rowLine: claim.rowLine,
      rowMeta: claim.rowMeta,
      ...(claim.nextCheck ? { nextCheck: claim.nextCheck } : {}),
      ...(claim.hitlItemId ? { hitlItemId: claim.hitlItemId } : {}),
    },
  };
  claim.stopped = stopped;
  claim.group = 'not-followed';
  claim.status = {
    kind,
    headline: fill.headline,
    ...(fill.bodyLines ? { bodyLines: fill.bodyLines } : {}),
    ...(claim.status.deadline ? { deadline: claim.status.deadline } : {}),
  };
  claim.rowBadge = kind === 'paused' ? 'paused' : 'not-following';
  claim.rowLine = fill.line;
  claim.rowMeta = ageText(claim);
  delete claim.hitlItemId;
  if (fill.nextCheck) claim.nextCheck = fill.nextCheck;
  else delete claim.nextCheck;
  claim.history = `${claim.history} · ${fill.verb} by ${IAR_USER.name} (${IAR_USER.org}) ${on}`;
}

export function stopFollowUp(id: string, opts: StopInput, flags: { quiet?: boolean } = {}): StopOutcome {
  const claim = state.claims.find((c) => c.id === id);
  if (!claim) return 'stays';
  const today = todayLabel();
  const iso = MOCK_NOW.toISOString().slice(0, 10);
  const note = opts.note?.trim() ?? '';
  const short = claim.payer.short;
  let outcome: StopOutcome = 'left';

  if (opts.reason === 'pause') {
    const untilIso = opts.pauseUntil ?? isoPlusDays(AR_TODAY_ISO, PAUSE_DEFAULT_DAYS);
    const until = isoLabel(untilIso);
    const nextCheck = isoLabel(nextBusinessDayIso(untilIso));
    const line = `Paused by ${IAR_USER.name} until ${until}${note !== '' ? ` · ${note}` : ''}`;
    moveToNotFollowed(claim, 'paused', {
      line,
      headline: `${line}. ELVA checks again on ${nextCheck}.`,
      verb: 'paused',
      until,
      nextCheck,
      note,
    });
    outcome = 'not-followed';
  } else if (opts.reason === 'billed_elsewhere') {
    const other = opts.otherPayer ?? 'another insurance';
    const otherShort = otherPayerShort(other);
    if (opts.billedWhy === 'coordination') {
      // The other insurance is the secondary: nothing stops, the claim just records the link.
      claim.linkedLine = `Linked to the ${otherShort} claim (secondary)`;
      outcome = 'stays';
    } else if (opts.billedWhy === 'wrong_insurance' && state.scenario === 'found-elsewhere') {
      // Mock: ELVA finds the other claim, so this one leaves the list.
      claim.removed = true;
    } else {
      moveToNotFollowed(claim, 'billed_elsewhere', {
        line: `Billed to ${otherShort}, says ${IAR_USER.name} · ELVA hasn’t found that claim yet`,
        headline: `Billed to ${otherShort}, says ${IAR_USER.name}.`,
        bodyLines: [
          `ELVA hasn’t found that claim yet, so ${short}’s ${money(claim.balance)} still counts as owed. ELVA looks again on every sync.`,
        ],
        verb: `billed to ${otherShort}`,
        otherPayer: other,
      });
      outcome = 'not-followed';
    }
  } else if (opts.reason === 'other') {
    const line = `Stopped by ${IAR_USER.name}${note !== '' ? ` · ${note}` : ''}`;
    moveToNotFollowed(claim, 'other', {
      line,
      headline: `Stopped by ${IAR_USER.name} on ${isoLabel(AR_TODAY_ISO)}.`,
      ...(note !== '' ? { bodyLines: [note] } : {}),
      verb: 'stopped',
      note,
    });
    outcome = 'not-followed';
  } else {
    // Write it off / the patient will pay it: the whole open amount, as the PMS shows it (never edited).
    const amount = moneyFor(claim).pmsPending;
    claim.removed = true;
    if (opts.reason === 'patient_pays') {
      // D164: ELVA moves the amount to the patient's balance; the record shows
      // "Moved to the patient · {date} · {name}". No PMS entry type is shown (O35).
      claim.movedToPatient = { date: today, by: IAR_USER.name, amount };
    } else {
      const why = note !== '' ? `${WRITE_OFF_REASON_WORDS.write_off}. ${note}` : WRITE_OFF_REASON_WORDS.write_off;
      state.writtenOff.unshift({
        id: `WO-${claim.id}`,
        recordId: claim.id,
        dateDisplay: today,
        iso,
        patient: claim.patient,
        visit: claim.visit,
        payer: claim.payer.name,
        why,
        by: IAR_ACTOR_DOT,
        amount,
      });
    }
  }
  if (!flags.quiet) emit();
  return outcome;
}

/** "Resume follow-up": a paused / stopped claim goes back to the group it came from. */
export function resumeFollowUp(id: string): void {
  const claim = state.claims.find((c) => c.id === id);
  const stopped = claim?.stopped;
  if (!claim || !stopped || stopped.kind === 'billed_elsewhere') return;
  const p = stopped.prior;
  claim.group = p.group;
  claim.status = p.status;
  claim.rowBadge = p.rowBadge;
  claim.rowLine = p.rowLine;
  claim.rowMeta = p.rowMeta;
  if (p.nextCheck) claim.nextCheck = p.nextCheck;
  else delete claim.nextCheck;
  if (p.hitlItemId) claim.hitlItemId = p.hitlItemId;
  delete claim.stopped;
  claim.history = `${claim.history} · resumed by ${IAR_USER.name} (${IAR_USER.org}) ${isoLabel(AR_TODAY_ISO)}`;
  applyLimit();
  emit();
}

/** "Moved to the patient · Oct 16 · Ava Thompson" — for the claim record (D164). */
export function getMovedToPatientText(claimId: string): string | null {
  const m = state.claims.find((c) => c.id === claimId)?.movedToPatient;
  return m ? `Moved to the patient · ${m.date} · ${m.by}` : null;
}

export function addWriteOffReason(id: string, reason: WriteOffReason, note?: string): void {
  const row = state.writtenOff.find((r) => r.id === id);
  if (!row) return;
  const note2 = note?.trim() ?? '';
  row.why = note2 !== '' ? `${WRITE_OFF_REASON_WORDS[reason]}. ${note2}` : WRITE_OFF_REASON_WORDS[reason];
  emit();
}
